import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Paper, Table, TableHead, TableRow, TableCell, Chip, IconButton, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, CircularProgress, Alert, Snackbar, Tooltip, Tabs, Tab,
  Switch, FormControlLabel,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ListIcon from '@mui/icons-material/List';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import {
  ADMIN_LOV_CATEGORIES_QUERY, ADMIN_LIST_OF_VALUES_QUERY,
  CREATE_LOV_MUTATION, UPDATE_LOV_MUTATION, DELETE_LOV_MUTATION,
} from '../graphql/queries';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const EMPTY_FORM = { category: '', value: '', label: '', sortOrder: 0 };

function LovDialog({ open, onClose, initial, categoryList, onSave, saving }) {
  const [form, setForm] = useState(initial);
  const isEdit = Boolean(initial.id);

  useEffect(() => { setForm(initial); }, [initial]);

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit LOV Entry' : 'New LOV Entry'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {!isEdit ? (
            <FormControl size="small" fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select value={form.category} label="Category" onChange={(e) => {
                const val = e.target.value;
                setForm(p => ({ ...p, category: val === '__new__' ? '' : val }));
              }}>
                {categoryList.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                <MenuItem value="__new__"><em>+ New category</em></MenuItem>
              </Select>
            </FormControl>
          ) : (
            <TextField label="Category" value={form.category} size="small" fullWidth disabled />
          )}
          {(!isEdit && (form.category === '' || !categoryList.includes(form.category))) && (
            <TextField label="New Category Name" value={form.category} onChange={f('category')}
              size="small" fullWidth required placeholder="e.g. physical_status" />
          )}
          <TextField label="Value (internal key)" value={form.value} onChange={f('value')}
            size="small" fullWidth required disabled={isEdit}
            placeholder="e.g. never_married" helperText={isEdit ? 'Value cannot be changed after creation' : 'Alphanumeric, underscores allowed'} />
          <TextField label="Label (display text)" value={form.label} onChange={f('label')}
            size="small" fullWidth required placeholder="e.g. Never Married" />
          <TextField label="Sort Order" value={form.sortOrder} onChange={f('sortOrder')}
            size="small" type="number" fullWidth InputProps={{ inputProps: { min: 0 } }} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)} disabled={saving || !form.category || !form.value || !form.label}>
          {saving ? <CircularProgress size={18} /> : (isEdit ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AdminLovPage() {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [dialog, setDialog] = useState({ open: false, item: EMPTY_FORM });
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const { data: catData, loading: catLoading, error: catError, refetch: refetchCats } =
    useQuery(ADMIN_LOV_CATEGORIES_QUERY);

  const [loadLovs, { data: lovData, loading: lovLoading, refetch: refetchLovs }] =
    useLazyQuery(ADMIN_LIST_OF_VALUES_QUERY, { fetchPolicy: 'network-only' });

  const [createLov] = useMutation(CREATE_LOV_MUTATION);
  const [updateLov] = useMutation(UPDATE_LOV_MUTATION);
  const [deleteLov] = useMutation(DELETE_LOV_MUTATION);

  const categories = catData?.adminLovCategories || [];
  const lovs = lovData?.adminListOfValues || [];

  useEffect(() => {
    if (categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0]);
    }
  }, [categories, selectedCategory]);

  useEffect(() => {
    if (selectedCategory) {
      loadLovs({ variables: { category: selectedCategory } });
    }
  }, [selectedCategory, loadLovs]);

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (form.id) {
        await updateLov({ variables: { id: form.id, label: form.label, sortOrder: parseInt(form.sortOrder, 10) || 0 } });
        showSnack('LOV entry updated');
      } else {
        await createLov({ variables: { category: form.category, value: form.value.trim(), label: form.label.trim(), sortOrder: parseInt(form.sortOrder, 10) || 0 } });
        showSnack('LOV entry created');
        // If newly created with a new category, update selected category
        if (!categories.includes(form.category)) {
          await refetchCats();
          setSelectedCategory(form.category);
        }
      }
      await refetchLovs?.({ category: selectedCategory || form.category });
      setDialog({ open: false, item: EMPTY_FORM });
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleToggleActive = async (lov) => {
    try {
      await updateLov({ variables: { id: lov.id, isActive: !lov.isActive } });
      await refetchLovs?.({ category: selectedCategory });
      showSnack(`LOV entry ${!lov.isActive ? 'activated' : 'deactivated'}`);
    } catch (e) { showSnack(e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    setSaving(true);
    try {
      await deleteLov({ variables: { id } });
      await refetchLovs?.({ category: selectedCategory });
      setConfirmDel(null);
      showSnack('LOV entry deleted');
    } catch (e) { showSnack(e.message, 'error'); }
    finally { setSaving(false); }
  };

  if (catLoading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;
  if (catError) return <Alert severity="error" sx={{ m: 3 }}>{catError.message}</Alert>;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <ListIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>List of Values (LOV)</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage dropdown options used throughout the app (religion, community, education, etc.)
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />}
          onClick={() => setDialog({ open: true, item: { ...EMPTY_FORM, category: selectedCategory || '' } })}>
          Add Entry
        </Button>
      </Stack>

      <Paper elevation={1} sx={{ borderRadius: 2, display: 'flex', minHeight: 500 }}>
        {/* Category sidebar */}
        <Box sx={{ width: 220, borderRight: 1, borderColor: 'divider', flexShrink: 0 }}>
          <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>CATEGORIES</Typography>
          </Box>
          <Box sx={{ overflow: 'auto', maxHeight: 500 }}>
            <Paged items={categories}>{rows => rows.map(cat => (
              <Box key={cat}
                onClick={() => setSelectedCategory(cat)}
                sx={{
                  px: 2, py: 1.2, cursor: 'pointer',
                  bgcolor: selectedCategory === cat ? 'primary.main' : 'transparent',
                  color: selectedCategory === cat ? 'white' : 'text.primary',
                  '&:hover': { bgcolor: selectedCategory === cat ? 'primary.dark' : 'action.hover' },
                  fontSize: 13, fontFamily: 'monospace',
                }}>
                {cat}
              </Box>
            ))}</Paged>
            {categories.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No categories yet</Typography>
            )}
          </Box>
        </Box>

        {/* LOV table */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {!selectedCategory ? (
            <Box display="flex" alignItems="center" justifyContent="center" height={300}>
              <Typography color="text.secondary">Select a category to view entries</Typography>
            </Box>
          ) : lovLoading ? (
            <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'grey.50' } }}>
                  <TableCell>Value</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell align="center">Sort</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lovs.map(lov => (
                  <TableRow key={lov.id} hover sx={{ opacity: lov.isActive ? 1 : 0.5 }}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{lov.value}</TableCell>
                    <TableCell>{lov.label}</TableCell>
                    <TableCell align="center">{lov.sortOrder}</TableCell>
                    <TableCell align="center">
                      <Switch size="small" checked={lov.isActive}
                        onChange={() => handleToggleActive(lov)} color="success" />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit label / sort order">
                        <IconButton size="small" color="primary"
                          onClick={() => setDialog({ open: true, item: { id: lov.id, category: lov.category, value: lov.value, label: lov.label, sortOrder: lov.sortOrder } })}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error"
                          onClick={() => setConfirmDel({ id: lov.id, label: lov.label })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {lovs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                      No entries for this category
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Box>
      </Paper>

      <LovDialog
        open={dialog.open}
        onClose={() => setDialog({ open: false, item: EMPTY_FORM })}
        initial={dialog.item}
        categoryList={categories}
        onSave={handleSave}
        saving={saving}
      />

      {/* Delete confirmation */}
      <Dialog open={Boolean(confirmDel)} onClose={() => setConfirmDel(null)} maxWidth="xs">
        <DialogTitle>Delete LOV Entry</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{confirmDel?.label}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(confirmDel.id)} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
